import { useState, useEffect, useCallback } from 'react'
import { MagnifyingGlass } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { userApi } from '@/api/users'

interface UserFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  roleSlug: string | undefined
  onRoleChange: (value: string | undefined) => void
  isActive: boolean | null
  onStatusChange: (value: boolean | null) => void
  orgId: string
}

export function UserFilters({
  search,
  onSearchChange,
  roleSlug,
  onRoleChange,
  isActive,
  onStatusChange,
  orgId,
}: UserFiltersProps) {
  const [localSearch, setLocalSearch] = useState(search)

  const { data: roles } = useQuery({
    queryKey: ['org-roles', orgId],
    queryFn: () => userApi.listRoles(orgId),
    staleTime: 10 * 60 * 1000,
    enabled: !!orgId,
  })

  // Debounced search -- 300ms
  const debouncedOnSearchChange = useCallback(
    (value: string) => {
      onSearchChange(value)
    },
    [onSearchChange]
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      debouncedOnSearchChange(localSearch)
    }, 300)
    return () => clearTimeout(timer)
  }, [localSearch, debouncedOnSearchChange])

  // Sync external search prop back to local state
  useEffect(() => {
    setLocalSearch(search)
  }, [search])

  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-1 max-w-sm">
        <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search users..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      <Select
        value={roleSlug ?? '__all__'}
        onValueChange={(v) => onRoleChange(v === '__all__' ? undefined : v)}
      >
        <SelectTrigger className="w-[160px] h-9">
          <SelectValue placeholder="All Roles" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Roles</SelectItem>
          {roles?.map((role) => (
            <SelectItem key={role.slug} value={role.slug}>
              {role.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={isActive === null ? '__all__' : isActive ? 'active' : 'inactive'}
        onValueChange={(v) => {
          if (v === '__all__') onStatusChange(null)
          else if (v === 'active') onStatusChange(true)
          else onStatusChange(false)
        }}
      >
        <SelectTrigger className="w-[140px] h-9">
          <SelectValue placeholder="All Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Status</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}
